import { useEffect, useState } from "react";
import { Container, Spinner, Card, Row, Col, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { usePage } from "../../hooks/usePage";
import { usePrecargaEvents } from "../../hooks/usePrecargaEvents";
import { legacyAdminGet } from "../../utils/PrecargaApi";
import FastTable from "../../components/FastTable/FastTable";

export default function PrecargasParticipantDetail() {
  usePage({ title: "Precargas - Detalle de Participante" });

  const { eventid, userId } = useParams();
  const navigate = useNavigate();
  const cashlessEventId = eventid ? parseInt(eventid) : null;

  const { precargaEvent, isLoadingPrecargaEvent } = usePrecargaEvents({
    cashlessEventId,
  });

  const [participant, setParticipant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (precargaEvent?.id && userId) {
      setIsLoading(true);
      legacyAdminGet(`${precargaEvent.id}/participants/${userId}/detail`)
        .then((data) => {
          setParticipant(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching participant detail:", error);
          setIsLoading(false);
        });
    }
  }, [precargaEvent?.id, userId]);

  if (isLoadingPrecargaEvent || isLoading) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-3">Cargando detalle de participante...</p>
        </div>
      </Container>
    );
  }

  if (!participant) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <p>No se encontró el participante</p>
          <Button onClick={() => navigate(`/${eventid}/preloads/participants`)}>
            Volver a Participantes
          </Button>
        </div>
      </Container>
    );
  }

  const orders = participant.orders || [];
  const cardLinks = participant.cardLinks || [];
  const stats = participant.stats || {};

  const totalOrders =
    participant.total_orders ??
    stats.totalOrders ??
    participant.totalOrders ??
    (Array.isArray(orders) ? orders.length : 0);

  const totalSpent =
    participant.total_spent ?? stats.totalSpent ?? participant.totalSpent ?? 0;

  const cardLinksCount =
    participant.card_links_count ??
    stats.cardLinksCount ??
    participant.cardLinksCount ??
    (Array.isArray(cardLinks) ? cardLinks.length : 0);

  const lastActivityAt =
    participant.last_activity_at ?? stats.lastActivityAt ?? participant.lastActivityAt ?? null;

  const balance =
    participant.balance ??
    stats.balance ??
    participant.wallet?.totalBalance ??
    participant.wallet?.balanceAccount ??
    0;

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const number = Number(value);
    if (Number.isNaN(number)) return "N/A";
    return `$${number.toLocaleString()}`;
  };

  const ordersColumns = [
    {
      label: "ID",
      key: "id",
      type: "number",
      width: 80,
    },
    {
      label: "Order ID",
      key: "orderId",
      type: "string",
      aggregate: (acc, row) => {
        let count = (parseInt(acc) || 0) + 1;
        if(count === 1) return "1 orden";
        return `${count} órdenes`;
      },
      aggregateInitialValue: "0 órdenes",
    },
    {
      label: "Monto",
      key: "amount",
      type: "currency",
      width: 120,
      aggregate: (acc, row) => acc + (parseFloat(row.amount) || 0),
      aggregateInitialValue: 0,
    },
    {
      label: "Estado",
      key: "status",
      type: "string",
      width: 120,
      filter: true,
    },
    {
      label: "Fecha",
      key: "createdAt",
      type: "date",
      width: 180,
    },
  ];

  const cardLinksColumns = [
    {
      label: "ID",
      key: "id",
      type: "number",
      width: 80,
    },
    {
      label: "Tarjeta NFC",
      key: "linkId",
      type: "string",
      aggregate: (acc, row) => {
        let count = (parseInt(acc) || 0) + 1;
        if(count === 1) return "1 link";
        return `${count} links`;
      },
      aggregateInitialValue: "0 links",
    },
    {
      label: "Estado",
      key: "status",
      type: "string",
      width: 120,
    },
    {
      label: "Fecha",
      key: "createdAt",
      type: "date",
      width: 180,
    },
  ];

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Button
            variant="secondary"
            onClick={() => navigate(`/${eventid}/preloads/participants`)}
          >
            ← Volver a Participantes
          </Button>
        </Col>
      </Row>

      <h2>Detalle de Participante</h2>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Información del Usuario</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>ID:</strong> {participant.user?.id}
              </p>
              <p>
                <strong>Email:</strong> {participant.user?.email}
              </p>
              <p>
                <strong>Teléfono:</strong> {participant.user?.phone || "N/A"}
              </p>
              <p>
                <strong>Email Verificado:</strong>{" "}
                {participant.user?.emailVerified ? "Sí" : "No"}
              </p>
              <p>
                <strong>Teléfono Verificado:</strong>{" "}
                {participant.user?.phoneVerified ? "Sí" : "No"}
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>
              <strong>Estadísticas</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Total de Órdenes:</strong> {totalOrders}
              </p>
              <p>
                <strong>Total Gastado:</strong> {formatMoney(totalSpent)}
              </p>
              <p>
                <strong>Saldo Actual:</strong> {formatMoney(balance)}
              </p>
              <p>
                <strong>Vinculaciones de Tarjetas:</strong>{" "}
                {cardLinksCount}
              </p>
              {lastActivityAt && (
                <p>
                  <strong>Última Actividad:</strong>{" "}
                  {new Date(lastActivityAt).toLocaleString()}
                </p>
              )}
            </Card.Body>
          </Card>

          {participant.wallet && (
            <Card className="mb-3">
              <Card.Header>
                <strong>Billetera</strong>
              </Card.Header>
              <Card.Body>
                <p>
                  <strong>Wallet ID:</strong> {participant.wallet.id}
                </p>
                <p>
                  <strong>Saldo en Cuenta:</strong> $
                  {participant.wallet.balanceAccount}
                </p>
                <p>
                  <strong>Saldo en Tarjeta:</strong> $
                  {participant.wallet.balanceCard}
                </p>
                <p>
                  <strong>Estado de Tarjeta:</strong>{" "}
                  {participant.wallet.cardStatus}
                </p>
                <Button
                  variant="link"
                  onClick={() =>
                    navigate(`/${eventid}/preloads/wallets/${participant.wallet.id}`)
                  }
                >
                  Ver Billetera
                </Button>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {orders.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h4>Órdenes ({orders.length})</h4>
            <FastTable
              title="Órdenes"
              data={orders}
              columns={ordersColumns}
              isLoading={false}
              isError={false}
              showExcel={false}
              showFilters={false}
              showPagination={false}
              onSelectRow={(row) =>
                navigate(`/${eventid}/preloads/orders/${row.orderId}`)
              }
            />
          </Col>
        </Row>
      )}

      {cardLinks.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h4>Vinculaciones de Tarjetas ({cardLinks.length})</h4>
            <FastTable
              title="Vinculaciones"
              data={cardLinks}
              columns={cardLinksColumns}
              isLoading={false}
              isError={false}
              showExcel={false}
              showFilters={false}
              showPagination={false}
              onSelectRow={(row) =>
                navigate(`/${eventid}/preloads/card-links/${row.linkId || row.id}`)
              }
            />
          </Col>
        </Row>
      )}
    </Container>
  );
}
